import React from 'react'
import { api } from '../lib/api.js'

function Carousel({ items }) {
  const [idx, setIdx] = React.useState(0)
  const safe = Array.isArray(items) ? items : []
  const cur = safe[idx] || null

  function prev() { setIdx((i) => (i - 1 + safe.length) % safe.length) }
  function next() { setIdx((i) => (i + 1) % safe.length) }

  if (!safe.length) return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-6 text-white/60">No screenshots configured yet.</div>
  )

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
      <div className="aspect-video bg-black/40 grid place-items-center">
        {/* eslint-disable-next-line */}
        <img src={cur.src} alt={cur.alt || 'screenshot'} className="w-full h-full object-cover" />
      </div>
      <div className="p-4 flex items-center justify-between gap-3">
        <div className="text-sm text-white/70 truncate">{cur.alt || `Screenshot ${idx + 1}`}</div>
        <div className="flex gap-2">
          <button onClick={prev} className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">Prev</button>
          <button onClick={next} className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">Next</button>
        </div>
      </div>
    </div>
  )
}

export default function Media() {
  const [media, setMedia] = React.useState(null)
  const [press, setPress] = React.useState(null)
  const [err, setErr] = React.useState('')

  React.useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const d = await api('/api/media')
        const p = await api('/api/presskit/meta')
        if (live) { setMedia(d.media || null); setPress(p) }
      } catch (e) {
        if (live) setErr(e.message || 'Unable to load media.')
      }
    })()
    return () => { live = false }
  }, [])

  const yt = media?.youtube?.videoId ? `https://www.youtube.com/embed/${media.youtube.videoId}` : null

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <div className="text-xs tracking-[0.32em] text-white/60">TRAILER & MEDIA</div>
      <h1 className="mt-2 text-3xl md:text-5xl font-semibold">Media</h1>
      <p className="mt-4 text-white/60 max-w-3xl">
        Trailer, screenshots, and short clips.
      </p>

      {err ? (
        <div className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{err}</div>
      ) : null}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-panel/60 glow p-6">
          <div className="text-xs tracking-[0.28em] text-white/50">TRAILER</div>
          <div className="mt-3 rounded-2xl overflow-hidden border border-white/10 bg-black/20">
            {yt ? (
              <iframe
                title={media?.youtube?.title || 'Trailer'}
                src={yt}
                className="w-full aspect-video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="aspect-video grid place-items-center text-white/60">Set youtube.videoId in admin.</div>
            )}
          </div>
          <div className="mt-4 text-sm text-white/60">{media?.youtube?.title || '—'}</div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-panel/60 glow p-6">
          <div className="text-xs tracking-[0.28em] text-white/50">PRESS KIT</div>
          <div className="mt-3 text-white/70 font-semibold">{press?.name || 'Press Kit'}</div>
          <div className="mt-2 text-sm text-white/60">{press?.notes || 'Logos, key art, and a fact sheet.'}</div>
          <div className="mt-3 text-xs text-white/45">Size: {press?.size_bytes ? `${Math.round(press.size_bytes/1024)} KB` : '—'}</div>
          <a
            href="/api/presskit/download"
            className="mt-5 inline-flex w-full justify-center rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm hover:bg-white/15"
          >
            Download Press Kit
          </a>
          <div className="mt-3 text-xs text-white/45">You can replace the ZIP in server/public/presskit/ and update media.json.</div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-panel/60 glow p-6">
          <div className="text-xs tracking-[0.28em] text-white/50">SCREENSHOTS</div>
          <div className="mt-4">
            <Carousel items={media?.screenshots || []} />
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-panel/60 glow p-6">
          <div className="text-xs tracking-[0.28em] text-white/50">GIF CLIPS</div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(media?.gifs || []).map((g, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-black/20 overflow-hidden">
                {/* eslint-disable-next-line */}
                <img src={g.src} alt={g.alt || `clip ${i+1}`} className="w-full h-40 object-cover" />
                <div className="px-3 py-2 text-xs text-white/60 truncate">{g.alt || 'Clip'}</div>
              </div>
            ))}
            {(!media?.gifs || !media.gifs.length) ? (
              <div className="rounded-xl border border-white/10 bg-black/20 p-4 text-white/60">No GIF clips configured yet.</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
